/*
This file is located in the main branch
Last time the file was updated (UTC): [11.12.2024 10:47:38]
Permissions to update outside the main branch: [false]
*/

import {
    DynamicListRowWrapperDefault,
    requiredRule,
    useUpdateEffect,
    ValidatedDynamicList,
    ValidatedDynamicListFormComponentProps,
    ValidatedInput,
} from '@finbeat/react-toolkit';
import { TradeOrgApiHttpHeaderInfo } from 'models/api/tradeOrg';
import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';
import { TradeOrgApiHeadersForm, TradeOrgApiHeadersFormWrapper } from './TradeOrgApiHeaders';
import { TradeOrgAddForm } from './TradeOrgChangeModal';

const TradeOrgChangeModalTabApi = ({ formHasRole }: { formHasRole: boolean }) => {
    const { t } = useTranslation();
    const { watch, setValue } = useFormContext<TradeOrgAddForm>();

    const useHttpHeaders = watch('apiSettings.useHttpHeaders'),
        useProxy = watch('apiSettings.useProxy');

    useUpdateEffect(() => {
        setValue('apiSettings.httpHeaders', [{ header: '', value: '' }]);
    }, [useHttpHeaders]);

    useUpdateEffect(() => {
        setValue('apiSettings.proxySettings.ip', '');
        setValue('apiSettings.proxySettings.port', '');
        setValue('apiSettings.proxySettings.authenticationMethod', '');
        setValue('apiSettings.proxySettings.login', '');
        setValue('apiSettings.proxySettings.password', '');
    }, [useProxy]);

    const formComponent = useCallback(
        (props: ValidatedDynamicListFormComponentProps) => (
            <TradeOrgApiHeadersForm {...props} formHasRole={formHasRole} />
        ),
        [formHasRole]
    );

    return (
        <>
            <ValidatedInput<TradeOrgAddForm>
                className="mt-3"
                label={t('BackOfficeUrl')}
                name={'apiSettings.url'}
                disabled={!formHasRole}
            />
            <Row>
                <Col>
                    <ValidatedInput<TradeOrgAddForm>
                        label={t('BackOfficeLogin')}
                        name="apiSettings.login"
                        disabled={!formHasRole}
                    />
                </Col>
                <Col>
                    <ValidatedInput<TradeOrgAddForm>
                        type="password"
                        name="apiSettings.password"
                        label={t('BackOfficePassword')}
                        disabled={!formHasRole}
                    />
                </Col>
            </Row>
            <ValidatedInput<TradeOrgAddForm>
                id="useHttpHeaders"
                type="checkbox"
                name="apiSettings.useHttpHeaders"
                label={t('BackOfficeAddHttpHeaders')}
                className="my-3"
                disabled={!formHasRole}
            />
            {useHttpHeaders && (
                <ValidatedDynamicList<TradeOrgAddForm, TradeOrgApiHttpHeaderInfo>
                    formComponent={formComponent}
                    wrapper={TradeOrgApiHeadersFormWrapper}
                    name="apiSettings.httpHeaders"
                    emptyRecord={{ header: '', value: '' }}
                    prepend={false}
                    rowWrapper={(props) => (
                        <DynamicListRowWrapperDefault
                            {...props}
                            disabled={props.disabled || props.idx === 0}
                        />
                    )}
                />
            )}
            <ValidatedInput<TradeOrgAddForm>
                id="useProxy"
                className="my-3"
                label={t('BackOfficeUseProxy')}
                name="apiSettings.useProxy"
                type="checkbox"
                disabled={!formHasRole}
            />
            {useProxy && (
                <>
                    <Row>
                        <Col xs="4">
                            <ValidatedInput<TradeOrgAddForm>
                                label={t('BackOfficeIp')}
                                name="apiSettings.proxySettings.ip"
                                disabled={!formHasRole}
                                rules={{ required: requiredRule }}
                            />
                        </Col>
                        <Col>
                            <ValidatedInput<TradeOrgAddForm>
                                label={t('BackOfficePort')}
                                name="apiSettings.proxySettings.port"
                                disabled={!formHasRole}
                                rules={{ required: requiredRule }}
                            />
                        </Col>
                        <Col xs="6">
                            <ValidatedInput<TradeOrgAddForm>
                                label={t('BackOfficeAuthMethod')}
                                name="apiSettings.proxySettings.authenticationMethod"
                                disabled={!formHasRole}
                                rules={{ required: requiredRule }}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <ValidatedInput<TradeOrgAddForm>
                                label={t('BackOfficeLogin')}
                                name="apiSettings.proxySettings.login"
                                disabled={!formHasRole}
                                rules={{ required: requiredRule }}
                            />
                        </Col>
                        <Col>
                            <ValidatedInput<TradeOrgAddForm>
                                label={t('BackOfficePassword')}
                                name="apiSettings.proxySettings.password"
                                type="password"
                                disabled={!formHasRole}
                                rules={{ required: requiredRule }}
                            />
                        </Col>
                    </Row>
                </>
            )}
            <ValidatedInput<TradeOrgAddForm>
                id="useOrderActualization"
                label={t('BackOfficeOrderActualization')}
                name="apiSettings.useOrderActualization"
                type="checkbox"
                disabled={!formHasRole}
            />
        </>
    );
};

export default TradeOrgChangeModalTabApi;
